import { ChevronDownIcon } from "lucide-react";
import type { ComponentType, ReactNode } from "react";
import { cn } from "../lib/utils.js";

export interface NavItem {
	id: string;
	label: string;
	icon: ComponentType<{
		className?: string;
		size?: number;
		strokeWidth?: number;
	}>;
	href?: string;
	count?: number;
	badge?: string;
	active?: boolean;
}

export interface NavSection {
	label: string;
	items: NavItem[];
}

export interface AdminSidebarProps {
	brandName: string;
	brandSub: string;
	/** Single letter or short string shown when brandLogoUrl is not provided */
	brandMark: string;
	/** URL to a brand logo image; replaces the text brandMark when provided */
	brandLogoUrl?: string;
	navSections: NavSection[];
	workspaceName: string;
	workspacePlan: string;
	workspaceInitials: string;
	/** Optional slot for bottom actions above the workspace card */
	footer?: ReactNode;
}

export function AdminSidebar({
	brandName,
	brandSub,
	brandMark,
	brandLogoUrl,
	navSections,
	workspaceName,
	workspacePlan,
	workspaceInitials,
	footer,
}: AdminSidebarProps) {
	return (
		<>
			{/* Brand */}
			<div className="flex h-[60px] flex-shrink-0 items-center gap-2.5 border-b border-sidebar-border px-5">
				{brandLogoUrl ? (
					<img
						src={brandLogoUrl}
						alt={brandName}
						className="h-8 w-auto max-w-[140px] object-contain"
					/>
				) : (
					<>
						<div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold bg-primary text-sidebar-primary dark:bg-sidebar-primary dark:text-primary">
							{brandMark}
						</div>
						<div className="min-w-0">
							<div className="truncate text-[15px] font-semibold leading-tight tracking-[-0.01em] text-sidebar-foreground">
								{brandName}
							</div>
							<span className="text-[10px] font-medium uppercase tracking-[0.12em] text-sidebar-foreground/50">
								{brandSub}
							</span>
						</div>
					</>
				)}
			</div>

			{/* Nav */}
			<nav className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 py-4">
				{navSections.map((section) => (
					<div key={section.label}>
						<div className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-foreground/40">
							{section.label}
						</div>
						<div className="flex flex-col gap-0.5">
							{section.items.map((item) => (
								<NavLink key={item.id} item={item} />
							))}
						</div>
					</div>
				))}
			</nav>

			{/* Footer */}
			{footer}
			<WorkspaceCard
				name={workspaceName}
				plan={workspacePlan}
				initials={workspaceInitials}
			/>
		</>
	);
}

function NavLink({ item }: { item: NavItem }) {
	const Icon = item.icon;
	return (
		<a
			href={item.href ?? "#"}
			className={cn(
				"group relative flex h-[34px] items-center gap-2.5 rounded-md px-3 text-[13.5px] font-medium leading-none transition-colors",
				item.active
					? "bg-sidebar-accent text-sidebar-foreground font-semibold"
					: "text-sidebar-foreground/60 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
			)}
		>
			{/* Active indicator */}
			{item.active && (
				<span
					className="absolute -left-3 top-[8px] bottom-[8px] w-[3px] rounded-r-[2px]"
					style={{ background: "var(--color-sidebar-primary)" }}
				/>
			)}
			<Icon
				className={cn(
					"h-[18px] w-[18px] flex-shrink-0",
					item.active
						? "text-sidebar-foreground"
						: "text-sidebar-foreground/50",
				)}
				strokeWidth={1.75}
			/>
			<span className="flex-1 truncate">{item.label}</span>
			{item.count !== undefined && (
				<span
					className={cn(
						"ml-auto text-[11px] tabular-nums",
						item.active
							? "text-sidebar-foreground/80"
							: "text-sidebar-foreground/60",
					)}
				>
					{item.count.toLocaleString()}
				</span>
			)}
			{item.badge && (
				<span className="ml-auto rounded-full bg-secondary px-1.5 py-0.5 text-[9.5px] font-bold tracking-[0.04em] text-secondary-foreground">
					{item.badge}
				</span>
			)}
		</a>
	);
}

function WorkspaceCard({
	name,
	plan,
	initials,
}: {
	name: string;
	plan: string;
	initials: string;
}) {
	return (
		<div className="flex-shrink-0 border-t border-sidebar-border p-4">
			<div className="flex items-center gap-2.5 rounded-[10px] border border-sidebar-border bg-sidebar-accent/50 p-3">
				<div
					className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-[13px] font-bold text-sidebar-primary"
					style={{
						background:
							"linear-gradient(135deg, var(--color-primary) 0%, color-mix(in oklch, var(--color-primary), white 25%) 100%)",
						color: "var(--color-sidebar-primary)",
					}}
				>
					{initials}
				</div>
				<div className="min-w-0 flex-1">
					<div className="truncate text-[12.5px] font-semibold leading-tight text-sidebar-foreground">
						{name}
					</div>
					<div className="text-[10.5px] text-sidebar-foreground/50">{plan}</div>
				</div>
				<ChevronDownIcon className="h-3.5 w-3.5 flex-shrink-0 text-sidebar-foreground/40" />
			</div>
		</div>
	);
}
